import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  ChevronRight,
  Clock3,
  Inbox,
  Layers3,
  LogOut,
  Plus,
  Search,
  ShieldCheck,
  Ticket as TicketIcon,
  Users,
} from "lucide-react";
import type { Session } from "@supabase/supabase-js";
import { configured, supabase } from "./lib/supabase";
import type { Profile, Ticket } from "./types";

type SlaSnapshot = {
  id: string;
  organization_id: string | null;
  period_start: string;
  opened: number;
  resolved: number;
  response_eligible: number;
  response_met: number;
  resolution_eligible: number;
  resolution_met: number;
  avg_response_minutes: number | null;
  avg_resolution_minutes: number | null;
};

type TicketComment = {
  id: string;
  ticket_id: string;
  author_id: string;
  body: string;
  internal: boolean;
  created_at: string;
  author?: { full_name: string; role: string } | null;
};

const demo: Ticket[] = [
  {
    id: "demo-1",
    ticket_number: "IDR-2026-1042",
    subject: "Payment webhook failing in production",
    description: "Webhook deliveries return HTTP 500 after deployment.",
    organization_id: "demo",
    customer_name: "Nexora Retail",
    requester_id: "demo",
    requester_name: "Priya Sharma",
    priority: "P1",
    status: "In Progress",
    support_level: "L2",
    assignee_id: "demo",
    assignee_name: "Arjun Rao",
    category: "Integration",
    created_at: new Date(Date.now() - 90 * 60000).toISOString(),
    first_response_at: new Date(Date.now() - 75 * 60000).toISOString(),
    response_due_at: new Date(Date.now() - 70 * 60000).toISOString(),
    resolution_due_at: new Date(Date.now() + 150 * 60000).toISOString(),
    resolved_at: null,
  },
  {
    id: "demo-2",
    ticket_number: "IDR-2026-1041",
    subject: "Dashboard export missing July records",
    description: "CSV contains only 24 of 31 expected days.",
    organization_id: "demo",
    customer_name: "Aster Finance",
    requester_id: "demo",
    requester_name: "Kabir Mehta",
    priority: "P2",
    status: "In Progress",
    support_level: "L1",
    assignee_id: "demo",
    assignee_name: "Neha Singh",
    category: "Reporting",
    created_at: new Date(Date.now() - 4 * 3600000).toISOString(),
    first_response_at: new Date(Date.now() - 3.6 * 3600000).toISOString(),
    response_due_at: new Date(Date.now() - 3 * 3600000).toISOString(),
    resolution_due_at: new Date(Date.now() + 4 * 3600000).toISOString(),
    resolved_at: null,
  },
  {
    id: "demo-3",
    ticket_number: "IDR-2026-1039",
    subject: "SSO role mapping not applied",
    description: "New users receive Viewer instead of Project Admin.",
    organization_id: "demo",
    customer_name: "Oryx Health",
    requester_id: "demo",
    requester_name: "Maya Iyer",
    priority: "P2",
    status: "Pending Customer",
    support_level: "L2",
    assignee_id: "demo",
    assignee_name: "Arjun Rao",
    category: "Access",
    created_at: new Date(Date.now() - 20 * 3600000).toISOString(),
    first_response_at: new Date(Date.now() - 19.5 * 3600000).toISOString(),
    response_due_at: new Date(Date.now() - 19 * 3600000).toISOString(),
    resolution_due_at: new Date(Date.now() + 10 * 3600000).toISOString(),
    resolved_at: null,
  },
  {
    id: "demo-4",
    ticket_number: "IDR-2026-1037",
    subject: "Unable to archive completed workspace",
    description: "Archive completes but workspace remains visible.",
    organization_id: "demo",
    customer_name: "Kite Labs",
    requester_id: "demo",
    requester_name: "Rohan Das",
    priority: "P3",
    status: "New",
    support_level: "L1",
    assignee_id: null,
    assignee_name: "Unassigned",
    category: "Workspace",
    created_at: new Date(Date.now() - 2 * 3600000).toISOString(),
    first_response_at: null,
    response_due_at: new Date(Date.now() + 2 * 3600000).toISOString(),
    resolution_due_at: new Date(Date.now() + 46 * 3600000).toISOString(),
    resolved_at: null,
  },
];

function timeLeft(iso: string) {
  const m = Math.round((new Date(iso).getTime() - Date.now()) / 60000),
    a = Math.abs(m);
  return {
    late: m < 0,
    risk: m >= 0 && m < 120,
    text: `${a >= 1440 ? Math.floor(a / 1440) + "d " : ""}${a >= 60 ? Math.floor((a % 1440) / 60) + "h " : ""}${a % 60}m ${m < 0 ? "overdue" : "left"}`,
  };
}

export default function App() {
  const [session, setSession] = useState<Session | null>(null),
    [profile, setProfile] = useState<Profile | null>(null),
    [tickets, setTickets] = useState<Ticket[]>(configured ? [] : demo),
    [snapshots, setSnapshots] = useState<SlaSnapshot[]>([]),
    [view, setView] = useState("overview"),
    [search, setSearch] = useState(""),
    [selected, setSelected] = useState<Ticket | null>(null),
    [comments, setComments] = useState<TicketComment[]>([]),
    [commentsLoading, setCommentsLoading] = useState(false),
    [commentText, setCommentText] = useState(""),
    [internalComment, setInternalComment] = useState(false),
    [postingComment, setPostingComment] = useState(false),
    [newOpen, setNewOpen] = useState(false),
    [loading, setLoading] = useState(true),
    [notice, setNotice] = useState("");
  useEffect(() => {
    if (!configured) {
      setLoading(false);
      return;
    }
    supabase.auth.getSession().then(({ data }) => {
      const s = data.session;
      setSession(
        s && sessionStorage.getItem("idrikta_verified_user") === s.user.id
          ? s
          : null,
      );
      setLoading(false);
    });
    const { data } = supabase.auth.onAuthStateChange((_e, s) => {
      if (!s) setSession(null);
      else if (sessionStorage.getItem("idrikta_verified_user") === s.user.id)
        setSession(s);
    });
    return () => data.subscription.unsubscribe();
  }, []);
  useEffect(() => {
    if (!session) return;
    (async () => {
      const [
        { data: p },
        { data: t, error },
        { data: s, error: snapshotError },
      ] = await Promise.all([
        supabase
          .from("profiles")
          .select("*")
          .eq("id", session.user.id)
          .single(),
        supabase.from("ticket_overview").select("*").order("resolution_due_at"),
        supabase
          .from("monthly_sla_snapshots")
          .select("*")
          .order("period_start", { ascending: false }),
      ]);
      if (p) setProfile(p);
      if (t) setTickets(t);
      if (s) setSnapshots(s);
      if (error || snapshotError)
        setNotice(
          (error || snapshotError)?.message || "Unable to load SLA data",
        );
    })();
  }, [session]);
  useEffect(() => {
    setComments([]);
    setCommentText("");
    setInternalComment(false);
    if (!session || !selected || selected.id.startsWith("demo")) return;
    setCommentsLoading(true);
    supabase
      .from("ticket_messages")
      .select(
        "*,author:profiles!ticket_messages_author_id_fkey(full_name,role)",
      )
      .eq("ticket_id", selected.id)
      .order("created_at", { ascending: true })
      .then(({ data, error }) => {
        if (data) setComments(data as TicketComment[]);
        if (error) setNotice(error.message);
        setCommentsLoading(false);
      });
  }, [session, selected?.id]);
  const visible = useMemo(
    () =>
      tickets.filter(
        (t) =>
          (!["L1", "L2", "L3"].includes(view) || t.support_level === view) &&
          `${t.ticket_number} ${t.subject} ${t.customer_name}`
            .toLowerCase()
            .includes(search.toLowerCase()),
      ),
    [tickets, view, search],
  );
  const open = tickets.filter(
    (t) => !["Resolved", "Closed"].includes(t.status),
  );
  const monthlySla = aggregateSnapshots(snapshots);
  const currentSla = monthlySla[0];
  const responseSla = slaPercent(
    currentSla?.response_met,
    currentSla?.response_eligible,
  );
  const resolutionSla = slaPercent(
    currentSla?.resolution_met,
    currentSla?.resolution_eligible,
  );
  const overallSla = (responseSla + resolutionSla) / 2;
  async function update(
    ticket: Ticket,
    action: "accept" | "escalate" | "resolve",
  ) {
    if (ticket.id.startsWith("demo")) {
      setNotice("Connect Supabase to persist changes");
      return;
    }
    const { data, error } = await supabase.rpc("transition_ticket", {
      p_ticket_id: ticket.id,
      p_action: action,
    });
    if (error) setNotice(error.message);
    else {
      const n = { ...ticket, ...data };
      setTickets((v) => v.map((t) => (t.id === ticket.id ? n : t)));
      setSelected(n);
      setNotice("Ticket updated");
    }
  }
  async function create(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!session) return;
    const f = Object.fromEntries(new FormData(e.currentTarget));
    const { data, error } = await supabase.rpc("create_ticket", {
      p_subject: f.subject,
      p_description: f.description,
      p_priority: f.priority,
      p_category: f.category,
      p_organization_id: profile?.organization_id,
    });
    if (error) setNotice(error.message);
    else {
      setTickets((v) => [data, ...v]);
      setNewOpen(false);
      setNotice(`${data.ticket_number} created`);
    }
  }
  async function postComment(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const body = commentText.trim();
    if (!session || !selected || !body || postingComment) return;
    setPostingComment(true);
    const { data, error } = await supabase
      .from("ticket_messages")
      .insert({
        ticket_id: selected.id,
        author_id: session.user.id,
        body,
        internal: profile?.role !== "customer" && internalComment,
      })
      .select(
        "*,author:profiles!ticket_messages_author_id_fkey(full_name,role)",
      )
      .single();
    if (error) setNotice(error.message);
    else {
      setComments((items) => [...items, data as TicketComment]);
      setCommentText("");
      setInternalComment(false);
      setNotice("Comment posted");
    }
    setPostingComment(false);
  }
  if (loading)
    return (
      <div className="splash">
        <img src="/idrikta-logo.png" />
        <span>Preparing support desk…</span>
      </div>
    );
  if (configured && !session) return <Login onVerified={setSession} />;
  return (
    <div className="app">
      <aside className="side">
        <div className="brand">
          <img src="/idrikta-logo.png" />
          <div>
            <b>idrikta</b>
            <span>SUPPORT DESK</span>
          </div>
        </div>
        <button className="primary" onClick={() => setNewOpen(true)}>
          <Plus size={17} />
          New ticket
        </button>
        <nav>
          {[
            ["overview", "Overview", BarChart3],
            ["tickets", "All tickets", TicketIcon],
            ["L1", "L1 queue", Inbox],
            ["L2", "L2 queue", Layers3],
            ["L3", "L3 queue", ShieldCheck],
            ["reports", "SLA reports", Clock3],
          ].map(([id, label, Icon]: any) => (
            <button
              className={view === id ? "active" : ""}
              onClick={() => setView(id)}
              key={id}
            >
              <Icon size={17} />
              <span>{label}</span>
              {String(id).startsWith("L") && (
                <em>{open.filter((t) => t.support_level === id).length}</em>
              )}
            </button>
          ))}
          {profile?.role === "admin" && (
            <button
              className={view === "admin" ? "active" : ""}
              onClick={() => setView("admin")}
            >
              <Users size={17} />
              <span>Administration</span>
            </button>
          )}
        </nav>
        <div className="user">
          <div>{profile?.full_name?.slice(0, 2).toUpperCase() || "ID"}</div>
          <span>
            <b>{profile?.full_name || "Idrikta Preview"}</b>
            <small>{profile?.role || "Demo mode"}</small>
          </span>
          {session && (
            <button
              onClick={() => {
                sessionStorage.removeItem("idrikta_verified_user");
                supabase.auth.signOut();
              }}
              title="Sign out"
            >
              <LogOut size={15} />
            </button>
          )}
        </div>
      </aside>
      <main>
        <header>
          <div>
            <h1>
              {view === "overview"
                ? "Support command centre"
                : view === "reports"
                  ? "SLA performance"
                  : view === "admin"
                    ? "Administration"
                    : view === "tickets"
                      ? "All tickets"
                      : `${view} support queue`}
            </h1>
            <p>
              {view === "overview"
                ? "Priorities, ownership and SLA health in one view."
                : view === "admin"
                  ? "Create customer organizations and secure user accounts."
                  : `${visible.length} tickets in this view`}
            </p>
          </div>
          <label className="search">
            <Search size={16} />
            <input
              placeholder="Search tickets…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
        </header>
        {view === "overview" && (
          <>
            <section className="metrics">
              <Metric
                label="Open tickets"
                value={String(open.length)}
                note="Across all queues"
                icon={<TicketIcon />}
              />
              <Metric
                label="Response SLA"
                value={formatPercent(responseSla)}
                note="Live monthly snapshot"
                icon={<Clock3 />}
              />
              <Metric
                label="Resolution SLA"
                value={formatPercent(resolutionSla)}
                note="Live monthly snapshot"
                icon={<CheckCircle2 />}
              />
              <Metric
                label="At risk"
                value={String(
                  open.filter((t) => timeLeft(t.resolution_due_at).risk).length,
                )}
                note="Needs attention"
                icon={<AlertTriangle />}
              />
            </section>
            <section className="grid">
              <div className="card">
                <CardTitle
                  title="Needs attention"
                  subtitle="Ordered by resolution SLA"
                />
                <TicketList items={open.slice(0, 6)} select={setSelected} />
              </div>
              <div className="card health">
                <CardTitle
                  title={`${currentSla ? formatPeriod(currentSla.period_start) : "Current month"} SLA health`}
                  subtitle="Live monthly performance"
                />
                <div
                  className="donut"
                  style={{
                    background: `conic-gradient(#09a68f 0 ${overallSla}%, #f15d69 ${overallSla}% 100%)`,
                  }}
                >
                  <div>
                    <b>{formatPercent(overallSla)}</b>
                    <span>overall</span>
                  </div>
                </div>
                <div className="legend">
                  <span>
                    <i className="green" />
                    Within SLA <b>{formatPercent(overallSla)}</b>
                  </span>
                  <span>
                    <i className="red" />
                    Breached <b>{formatPercent(100 - overallSla)}</b>
                  </span>
                </div>
              </div>
            </section>
            <section className="card queues">
              <CardTitle
                title="Team queues"
                subtitle="Workload by support level"
              />
              <div>
                {["L1", "L2", "L3"].map((l) => (
                  <button key={l} onClick={() => setView(l)}>
                    <i>{l}</i>
                    <span>
                      <b>{l} Support</b>
                      <small>
                        {l === "L1"
                          ? "Triage & first response"
                          : l === "L2"
                            ? "Technical investigation"
                            : "Engineering & product"}
                      </small>
                    </span>
                    <strong>
                      {open.filter((t) => t.support_level === l).length}
                      <small>open</small>
                    </strong>
                    <ChevronRight />
                  </button>
                ))}
              </div>
            </section>
          </>
        )}
        {view !== "overview" && view !== "reports" && view !== "admin" && (
          <section className="card list">
            <CardTitle
              title={view === "tickets" ? "Ticket register" : `${view} queue`}
              subtitle="Select a ticket to view and act"
            />
            <TicketList items={visible} select={setSelected} />
          </section>
        )}
        {view === "reports" && <Reports snapshots={snapshots} />}
        {view === "admin" && profile?.role === "admin" && <AdminPanel />}
      </main>
      {selected && (
        <div
          className="shade"
          onMouseDown={(e) => e.currentTarget === e.target && setSelected(null)}
        >
          <aside className="drawer">
            <button className="x" onClick={() => setSelected(null)}>
              ×
            </button>
            <small>
              {selected.ticket_number} · {selected.support_level}
            </small>
            <h2>{selected.subject}</h2>
            <div className="tags">
              <i className={selected.priority}>{selected.priority}</i>
              <span>{selected.status}</span>
              <span>{selected.category}</span>
            </div>
            <p>{selected.description}</p>
            <dl>
              <div>
                <dt>Customer</dt>
                <dd>{selected.customer_name}</dd>
              </div>
              <div>
                <dt>Requester</dt>
                <dd>{selected.requester_name}</dd>
              </div>
              <div>
                <dt>Assignee</dt>
                <dd>{selected.assignee_name || "Unassigned"}</dd>
              </div>
              <div>
                <dt>Created</dt>
                <dd>{new Date(selected.created_at).toLocaleString()}</dd>
              </div>
            </dl>
            <section className="sla">
              <div>
                <small>Response SLA</small>
                <b>
                  {selected.first_response_at
                    ? "Responded"
                    : timeLeft(selected.response_due_at).text}
                </b>
              </div>
              <div>
                <small>Resolution SLA</small>
                <b>
                  {selected.resolved_at
                    ? "Resolved"
                    : timeLeft(selected.resolution_due_at).text}
                </b>
              </div>
            </section>
            <section className="conversation">
              <div className="conversation-title">
                <div>
                  <b>Conversation</b>
                  <small>Customer and support updates</small>
                </div>
                <span>{comments.length}</span>
              </div>
              <div className="comments">
                {commentsLoading && (
                  <div className="comment-empty">Loading conversation…</div>
                )}
                {!commentsLoading &&
                  comments.map((comment) => (
                    <article
                      className={comment.internal ? "internal" : ""}
                      key={comment.id}
                    >
                      <div>
                        <b>{comment.author?.full_name || "Support user"}</b>
                        <span>{comment.author?.role || "user"}</span>
                        {comment.internal && <em>Internal note</em>}
                        <time>
                          {new Date(comment.created_at).toLocaleString()}
                        </time>
                      </div>
                      <p>{comment.body}</p>
                    </article>
                  ))}
                {!commentsLoading && !comments.length && (
                  <div className="comment-empty">
                    No comments yet. Start the conversation below.
                  </div>
                )}
              </div>
              <form className="comment-form" onSubmit={postComment}>
                <textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Add more details, ask a question, or share a status update…"
                  rows={3}
                  maxLength={5000}
                  required
                />
                <div>
                  {profile?.role !== "customer" && (
                    <label>
                      <input
                        type="checkbox"
                        checked={internalComment}
                        onChange={(e) => setInternalComment(e.target.checked)}
                      />
                      Internal note (hidden from customer)
                    </label>
                  )}
                  <button disabled={postingComment || !commentText.trim()}>
                    {postingComment ? "Posting…" : "Post comment"}
                  </button>
                </div>
              </form>
            </section>
            <footer>
              {selected.status === "New" && (
                <button onClick={() => update(selected, "accept")}>
                  Accept
                </button>
              )}
              {selected.support_level !== "L3" && !selected.resolved_at && (
                <button
                  className="soft"
                  onClick={() => update(selected, "escalate")}
                >
                  Escalate
                </button>
              )}
              {!selected.resolved_at && (
                <button
                  className="success"
                  onClick={() => update(selected, "resolve")}
                >
                  Resolve
                </button>
              )}
            </footer>
          </aside>
        </div>
      )}
      {newOpen && (
        <div className="shade">
          <form className="modal" onSubmit={create}>
            <button
              type="button"
              className="x"
              onClick={() => setNewOpen(false)}
            >
              ×
            </button>
            <small>NEW SUPPORT REQUEST</small>
            <h2>Create ticket</h2>
            <label>
              Subject
              <input name="subject" required />
            </label>
            <div>
              <label>
                Priority
                <select name="priority" defaultValue="P3">
                  <option>P0</option>
                  <option>P1</option>
                  <option>P2</option>
                  <option>P3</option>
                  <option>P4</option>
                </select>
              </label>
              <label>
                Category
                <select name="category">
                  <option>General</option>
                  <option>Integration</option>
                  <option>Access</option>
                  <option>Reporting</option>
                  <option>Performance</option>
                </select>
              </label>
            </div>
            <label>
              Description
              <textarea name="description" rows={5} />
            </label>
            <footer>
              <button
                type="button"
                className="soft"
                onClick={() => setNewOpen(false)}
              >
                Cancel
              </button>
              <button className="primary">Create ticket</button>
            </footer>
          </form>
        </div>
      )}
      {notice && (
        <button className="toast" onClick={() => setNotice("")}>
          ✓ {notice}
        </button>
      )}
    </div>
  );
}

function Login({ onVerified }: { onVerified: (s: Session) => void }) {
  const [userId, setUserId] = useState(""),
    [clientCode, setClientCode] = useState(""),
    [password, setPassword] = useState(""),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMessage("");
    const code = clientCode.trim().toLowerCase(),
      enteredUser = userId.trim().toLowerCase(),
      shortUser = enteredUser.split("@")[0];
    const email = `${shortUser}@${code}.support.idrikta.internal`;
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) {
      setMessage("Invalid user ID, password or client code");
      setBusy(false);
      return;
    }
    const { data: valid, error: verifyError } = await supabase.rpc(
      "verify_client_code",
      { p_code: code },
    );
    if (verifyError || !valid) {
      await supabase.auth.signOut();
      setMessage("This user ID is not assigned to that client code");
      setBusy(false);
      return;
    }
    sessionStorage.setItem("idrikta_verified_user", data.session.user.id);
    onVerified(data.session);
    setBusy(false);
  }
  return (
    <div className="login">
      <section>
        <img src="/idrikta-logo.png" />
        <small>INTELLIGENT VISION. ASSURED QUALITY.</small>
      </section>
      <form onSubmit={submit}>
        <span>SECURE SUPPORT PORTAL</span>
        <h1>Welcome back</h1>
        <p>
          Enter your Idrikta user ID and the client code assigned to your
          organization.
        </p>
        <label>
          User ID
          <input
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            autoComplete="username"
            placeholder="e.g. testuser"
            required
          />
        </label>
        <label>
          Client / organization code
          <input
            value={clientCode}
            onChange={(e) =>
              setClientCode(e.target.value.replace(/[^a-zA-Z0-9-]/g, ""))
            }
            autoCapitalize="none"
            placeholder="e.g. ccrt"
            required
          />
        </label>
        <label>
          Password
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </label>
        {message && <div className="error">{message}</div>}
        <button className="primary" disabled={busy}>
          {busy ? "Verifying…" : "Sign in securely"}
        </button>
        <small className="login-help">
          Enter only your user ID; the organization code completes your secure
          identity.
        </small>
      </form>
    </div>
  );
}
function Metric({
  label,
  value,
  note,
  icon,
}: {
  label: string;
  value: string;
  note: string;
  icon: React.ReactNode;
}) {
  return (
    <article>
      <div>
        <span>{label}</span>
        <i>{icon}</i>
      </div>
      <b>{value}</b>
      <small>{note}</small>
    </article>
  );
}
function CardTitle({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="card-title">
      <h2>
        {title}
        <small>{subtitle}</small>
      </h2>
    </div>
  );
}
function TicketList({
  items,
  select,
}: {
  items: Ticket[];
  select: (t: Ticket) => void;
}) {
  return (
    <div className="ticket-list">
      {items.map((t) => {
        const s = timeLeft(t.resolution_due_at);
        return (
          <button key={t.id} onClick={() => select(t)}>
            <i className={t.priority}>{t.priority}</i>
            <span>
              <b>{t.subject}</b>
              <small>
                {t.ticket_number} · {t.customer_name}
              </small>
            </span>
            <em className={s.late ? "late" : s.risk ? "risk" : ""}>
              <b>{t.resolved_at ? "SLA met" : s.text}</b>
              <small>
                {t.status} · {t.support_level}
              </small>
            </em>
            <ChevronRight size={16} />
          </button>
        );
      })}
      {!items.length && <div className="empty">No tickets in this view.</div>}
    </div>
  );
}
type OrganizationOption = {
  id: string;
  name: string;
  slug: string;
  active: boolean;
};
type ManagedUser = {
  id: string;
  user_id: string;
  full_name: string;
  role: string;
  organization_id: string | null;
  organization_name: string | null;
  client_code: string;
  active: boolean;
  created_at: string;
  last_sign_in_at: string | null;
};
type ManagedSla = {
  id?: string;
  priority: string;
  response_minutes: number;
  resolution_minutes: number;
  coverage_24x7: boolean;
  pause_on_customer: boolean;
  active: boolean;
  inherited: boolean;
};
function AdminPanel() {
  const [organizations, setOrganizations] = useState<OrganizationOption[]>([]);
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [editOrganization, setEditOrganization] =
    useState<OrganizationOption | null>(null);
  const [editUser, setEditUser] = useState<ManagedUser | null>(null);
  const [slaOrganization, setSlaOrganization] = useState("");
  const [slaPolicies, setSlaPolicies] = useState<ManagedSla[]>([]);
  const [role, setRole] = useState("customer");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function loadOrganizations() {
    const { data, error } = await supabase
      .from("organizations")
      .select("id,name,slug,active")
      .order("name");
    if (data) {
      setOrganizations(data);
      if (!slaOrganization && data.length) {
        setSlaOrganization(data[0].id);
        loadSla(data[0].id);
      }
    }
    if (error) setError(error.message);
  }
  async function loadUsers() {
    const { data, error } = await supabase.functions.invoke(
      "admin-management",
      { body: { action: "list_users" } },
    );
    if (data?.users) setUsers(data.users);
    if (error) setError(error.message);
  }
  async function loadSla(organizationId: string) {
    if (!organizationId) {
      setSlaPolicies([]);
      return;
    }
    const { data, error } = await supabase.functions.invoke(
      "admin-management",
      { body: { action: "list_sla", organization_id: organizationId } },
    );
    if (data?.policies) setSlaPolicies(data.policies);
    if (error) setError(error.message);
  }
  useEffect(() => {
    loadOrganizations();
    loadUsers();
  }, []);
  async function invoke(body: Record<string, unknown>) {
    setBusy(true);
    setError("");
    setMessage("");
    const { data, error } = await supabase.functions.invoke(
      "admin-management",
      { body },
    );
    setBusy(false);
    if (error) {
      setError(error.message);
      return null;
    }
    if (data?.error) {
      setError(data.error);
      return null;
    }
    return data;
  }
  async function createOrganization(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const values = Object.fromEntries(new FormData(form));
    const data = await invoke({
      action: "create_organization",
      name: values.organization_name,
      code: values.organization_code,
    });
    if (data) {
      setMessage(
        `Organization ${data.organization.name} (${data.organization.slug}) created.`,
      );
      form.reset();
      await loadOrganizations();
    }
  }
  async function createUser(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const values = Object.fromEntries(new FormData(form));
    const data = await invoke({
      action: "create_user",
      user_id: values.user_id,
      full_name: values.full_name,
      password: values.password,
      role: values.role,
      organization_id: values.organization_id || null,
    });
    if (data) {
      setMessage(
        `User created. User ID: ${data.credentials.user_id} · Client code: ${data.credentials.client_code}`,
      );
      form.reset();
      setRole("customer");
      await loadUsers();
    }
  }
  async function updateOrganization(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!editOrganization) return;
    const values = Object.fromEntries(new FormData(e.currentTarget));
    const data = await invoke({
      action: "update_organization",
      id: editOrganization.id,
      name: values.organization_name,
      active: values.active === "on",
    });
    if (data) {
      setMessage(`Organization ${data.organization.name} updated.`);
      setEditOrganization(null);
      await loadOrganizations();
    }
  }
  async function updateUser(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!editUser) return;
    const values = Object.fromEntries(new FormData(e.currentTarget));
    const data = await invoke({
      action: "update_user",
      id: editUser.id,
      full_name: values.full_name,
      role: values.role,
      organization_id: values.organization_id || null,
      active: values.active === "on",
      password: values.password || "",
    });
    if (data) {
      setMessage(
        `User ${data.credentials.user_id} updated. Client code: ${data.credentials.client_code}`,
      );
      setEditUser(null);
      await loadUsers();
    }
  }
  async function saveSla(
    e: React.FormEvent<HTMLFormElement>,
    policy: ManagedSla,
  ) {
    e.preventDefault();
    const values = Object.fromEntries(new FormData(e.currentTarget));
    const data = await invoke({
      action: "upsert_sla",
      organization_id: slaOrganization,
      priority: policy.priority,
      response_minutes: Number(values.response_minutes),
      resolution_minutes: Number(values.resolution_minutes),
      coverage_24x7: values.coverage_24x7 === "on",
      pause_on_customer: values.pause_on_customer === "on",
    });
    if (data) {
      setMessage(
        `${policy.priority} SLA saved for ${organizations.find((o) => o.id === slaOrganization)?.name}.`,
      );
      await loadSla(slaOrganization);
    }
  }
  return (
    <section className="admin-grid">
      <form className="admin-card" onSubmit={createOrganization}>
        <div className="admin-card-title">
          <b>Add organization</b>
          <small>Create a new customer tenant and client code.</small>
        </div>
        <label>
          Organization name
          <input
            name="organization_name"
            placeholder="Example Corporation"
            minLength={2}
            required
          />
        </label>
        <label>
          Client / organization code
          <input
            name="organization_code"
            placeholder="EXAMPLE"
            pattern="[A-Za-z0-9-]{2,20}"
            title="2–20 letters, numbers or hyphens"
            required
          />
        </label>
        <button className="primary" disabled={busy}>
          Create organization
        </button>
      </form>
      <form className="admin-card" onSubmit={createUser}>
        <div className="admin-card-title">
          <b>Add user</b>
          <small>Create authentication details and assign access.</small>
        </div>
        <div className="admin-fields">
          <label>
            Full name
            <input
              name="full_name"
              placeholder="User's full name"
              minLength={2}
              required
            />
          </label>
          <label>
            User ID
            <input
              name="user_id"
              placeholder="e.g. anand"
              pattern="[A-Za-z0-9._-]{3,40}"
              title="3–40 letters, numbers, dots, underscores or hyphens"
              required
            />
          </label>
        </div>
        <div className="admin-fields">
          <label>
            Role
            <select
              name="role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              <option value="customer">Customer</option>
              <option value="l1">L1 Engineer</option>
              <option value="l2">L2 Engineer</option>
              <option value="l3">L3 Engineer</option>
              <option value="manager">Manager</option>
              <option value="admin">Administrator</option>
            </select>
          </label>
          <label>
            Organization
            <select
              name="organization_id"
              required={role === "customer"}
              disabled={role !== "customer"}
            >
              <option value="">Select organization</option>
              {organizations
                .filter((o) => o.active)
                .map((o) => (
                  <option value={o.id} key={o.id}>
                    {o.name} ({o.slug})
                  </option>
                ))}
            </select>
          </label>
        </div>
        <label>
          Temporary password
          <input
            name="password"
            type="password"
            minLength={10}
            autoComplete="new-password"
            required
          />
        </label>
        <button className="primary" disabled={busy}>
          Create user
        </button>
      </form>
      {(message || error) && (
        <div className={error ? "admin-result error" : "admin-result"}>
          {error || message}
        </div>
      )}
      <div className="admin-card org-register">
        <div className="admin-card-title">
          <b>Organizations</b>
          <small>{organizations.length} customer tenants configured</small>
        </div>
        {organizations.map((o) => (
          <div className="manage-row" key={o.id}>
            <span>
              <b>{o.name}</b>
              <small>{o.slug}</small>
            </span>
            <div>
              <em className={o.active ? "active" : ""}>
                {o.active ? "Active" : "Inactive"}
              </em>
              <button onClick={() => setEditOrganization(o)}>Edit</button>
            </div>
          </div>
        ))}
      </div>
      <div className="admin-card org-register user-register">
        <div className="admin-card-title">
          <b>Users</b>
          <small>{users.length} authentication accounts configured</small>
        </div>
        {users.map((user) => (
          <div className="manage-row" key={user.id}>
            <span>
              <b>{user.full_name}</b>
              <small>
                {user.user_id} · {user.client_code} ·{" "}
                {user.organization_name || "Idrikta support"}
              </small>
            </span>
            <div>
              <em className={user.active ? "active" : ""}>
                {user.active ? user.role : "Inactive"}
              </em>
              <button onClick={() => setEditUser(user)}>Edit</button>
            </div>
          </div>
        ))}
      </div>
      <div className="admin-card sla-admin">
        <div className="admin-card-title">
          <b>Client SLA policies</b>
          <small>
            Set response and resolution targets independently for every
            customer.
          </small>
        </div>
        <label>
          Organization
          <select
            value={slaOrganization}
            onChange={(e) => {
              setSlaOrganization(e.target.value);
              loadSla(e.target.value);
            }}
          >
            <option value="">Select organization</option>
            {organizations
              .filter((o) => o.active)
              .map((o) => (
                <option value={o.id} key={o.id}>
                  {o.name} ({o.slug})
                </option>
              ))}
          </select>
        </label>
        <div className="sla-policy-head">
          <span>Priority</span>
          <span>Response (minutes)</span>
          <span>Resolution (minutes)</span>
          <span>Coverage</span>
          <span></span>
        </div>
        {slaPolicies.map((policy) => (
          <form
            className="sla-policy-row"
            key={`${slaOrganization}-${policy.priority}`}
            onSubmit={(e) => saveSla(e, policy)}
          >
            <strong className={policy.priority}>{policy.priority}</strong>
            <label>
              <input
                name="response_minutes"
                type="number"
                min={1}
                defaultValue={policy.response_minutes}
                required
              />
            </label>
            <label>
              <input
                name="resolution_minutes"
                type="number"
                min={1}
                defaultValue={policy.resolution_minutes}
                required
              />
            </label>
            <div className="sla-flags">
              <label>
                <input
                  name="coverage_24x7"
                  type="checkbox"
                  defaultChecked={policy.coverage_24x7}
                />
                24×7
              </label>
              <label>
                <input
                  name="pause_on_customer"
                  type="checkbox"
                  defaultChecked={policy.pause_on_customer}
                />
                Pause on customer
              </label>
            </div>
            <button disabled={busy}>
              {policy.inherited ? "Create override" : "Save"}
            </button>
            {policy.inherited && <small>Using global default</small>}
          </form>
        ))}
      </div>
      {editOrganization && (
        <div className="shade">
          <form className="modal admin-edit" onSubmit={updateOrganization}>
            <button
              type="button"
              className="x"
              onClick={() => setEditOrganization(null)}
            >
              ×
            </button>
            <small>EDIT ORGANIZATION</small>
            <h2>{editOrganization.name}</h2>
            <label>
              Organization name
              <input
                name="organization_name"
                defaultValue={editOrganization.name}
                minLength={2}
                required
              />
            </label>
            <label>
              Client code
              <input value={editOrganization.slug} disabled />
              <small>
                Client codes are fixed after creation to protect existing login
                IDs.
              </small>
            </label>
            <label className="active-check">
              <input
                name="active"
                type="checkbox"
                defaultChecked={editOrganization.active}
              />
              Organization is active
            </label>
            <footer>
              <button
                type="button"
                className="soft"
                onClick={() => setEditOrganization(null)}
              >
                Cancel
              </button>
              <button disabled={busy}>Save changes</button>
            </footer>
          </form>
        </div>
      )}
      {editUser && (
        <div className="shade">
          <form className="modal admin-edit" onSubmit={updateUser}>
            <button
              type="button"
              className="x"
              onClick={() => setEditUser(null)}
            >
              ×
            </button>
            <small>EDIT USER</small>
            <h2>{editUser.user_id}</h2>
            <label>
              Full name
              <input
                name="full_name"
                defaultValue={editUser.full_name}
                minLength={2}
                required
              />
            </label>
            <div>
              <label>
                Role
                <select
                  name="role"
                  value={editUser.role}
                  onChange={(e) =>
                    setEditUser({
                      ...editUser,
                      role: e.target.value,
                      organization_id:
                        e.target.value === "customer"
                          ? editUser.organization_id
                          : null,
                    })
                  }
                >
                  <option value="customer">Customer</option>
                  <option value="l1">L1 Engineer</option>
                  <option value="l2">L2 Engineer</option>
                  <option value="l3">L3 Engineer</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Administrator</option>
                </select>
              </label>
              <label>
                Organization
                <select
                  name="organization_id"
                  value={editUser.organization_id || ""}
                  onChange={(e) =>
                    setEditUser({
                      ...editUser,
                      organization_id: e.target.value || null,
                    })
                  }
                  required={editUser.role === "customer"}
                  disabled={editUser.role !== "customer"}
                >
                  <option value="">Select organization</option>
                  {organizations
                    .filter((o) => o.active)
                    .map((o) => (
                      <option value={o.id} key={o.id}>
                        {o.name} ({o.slug})
                      </option>
                    ))}
                </select>
              </label>
            </div>
            <label>
              New password (optional)
              <input
                name="password"
                type="password"
                minLength={10}
                autoComplete="new-password"
                placeholder="Leave blank to keep current password"
              />
            </label>
            <label className="active-check">
              <input
                name="active"
                type="checkbox"
                defaultChecked={editUser.active}
              />
              User account is active
            </label>
            <footer>
              <button
                type="button"
                className="soft"
                onClick={() => setEditUser(null)}
              >
                Cancel
              </button>
              <button disabled={busy}>Save changes</button>
            </footer>
          </form>
        </div>
      )}
    </section>
  );
}
function slaPercent(met = 0, eligible = 0) {
  return eligible > 0 ? (met / eligible) * 100 : 0;
}
function formatPercent(value: number) {
  return `${value.toFixed(2)}%`;
}
function formatPeriod(period: string) {
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${period}T00:00:00Z`));
}
function formatMinutes(value: number | null) {
  if (value == null) return "—";
  const minutes = Math.round(value),
    hours = Math.floor(minutes / 60);
  return hours ? `${hours}h ${minutes % 60}m` : `${minutes}m`;
}
function aggregateSnapshots(rows: SlaSnapshot[]) {
  const grouped = new Map<string, SlaSnapshot>();
  for (const row of rows) {
    const existing = grouped.get(row.period_start);
    if (!existing) {
      grouped.set(row.period_start, { ...row });
      continue;
    }
    const resolutionWeight =
      existing.resolution_eligible + row.resolution_eligible;
    existing.avg_resolution_minutes = resolutionWeight
      ? ((existing.avg_resolution_minutes || 0) * existing.resolution_eligible +
          (row.avg_resolution_minutes || 0) * row.resolution_eligible) /
        resolutionWeight
      : null;
    existing.opened += row.opened;
    existing.resolved += row.resolved;
    existing.response_eligible += row.response_eligible;
    existing.response_met += row.response_met;
    existing.resolution_eligible += row.resolution_eligible;
    existing.resolution_met += row.resolution_met;
  }
  return [...grouped.values()].sort((a, b) =>
    b.period_start.localeCompare(a.period_start),
  );
}
function Reports({ snapshots }: { snapshots: SlaSnapshot[] }) {
  const rows = aggregateSnapshots(snapshots),
    current = rows[0];
  return (
    <>
      <section className="metrics report-metrics">
        <Metric
          label="Tickets this month"
          value={String(current?.opened || 0)}
          note="Across all support levels"
          icon={<TicketIcon />}
        />
        <Metric
          label="Response SLA"
          value={formatPercent(
            slaPercent(current?.response_met, current?.response_eligible),
          )}
          note="Target 95%"
          icon={<Clock3 />}
        />
        <Metric
          label="Resolution SLA"
          value={formatPercent(
            slaPercent(current?.resolution_met, current?.resolution_eligible),
          )}
          note="Target 90%"
          icon={<CheckCircle2 />}
        />
      </section>
      <section className="card report">
        <CardTitle
          title="Monthly SLA summary"
          subtitle="Auditable performance snapshots"
        />
        <div className="report-head">
          <span>Period</span>
          <span>Opened</span>
          <span>Response</span>
          <span>Resolution</span>
          <span>MTTR</span>
        </div>
        {rows.map((r) => (
          <div className="report-row" key={r.period_start}>
            <b>{formatPeriod(r.period_start)}</b>
            <span>{r.opened}</span>
            <span>
              {formatPercent(slaPercent(r.response_met, r.response_eligible))}
            </span>
            <span>
              {formatPercent(
                slaPercent(r.resolution_met, r.resolution_eligible),
              )}
            </span>
            <span>{formatMinutes(r.avg_resolution_minutes)}</span>
          </div>
        ))}
        {!rows.length && (
          <div className="empty">No monthly SLA snapshot is available.</div>
        )}
      </section>
    </>
  );
}
